/**
 * Client-side prediction with server reconciliation using Rapier 2D physics.
 * Maintains a persistent RapierWorld for fast reconciliation (no world rebuild per frame).
 */

import {
  initRapier,
  RapierWorld,
  resolveInput,
  applyShipInput,
  extractShipAfterStep,
  PLANET_X,
  PLANET_Y,
  MOON_ORBIT_RADIUS,
  MOON_ORBIT_SPEED,
  TICK_DELTA,
  ASTEROID_MASS_PER_RADIUS,
  INPUT_THRUST,
  INPUT_AFTERBURNER,
  INPUT_BRAKE,
  INPUT_STRAFE_LEFT,
  INPUT_STRAFE_RIGHT,
  INPUT_FIRE,
} from '@warmancer/shared';
import type { ShipState, MutableShipState, InputFrame, WorldSetup, DebugPhysicsState } from '@warmancer/shared';
import type { RemotePlayer } from '../../stores/gameStore';
import { recordReconciliation } from './ReconciliationLog';
import { recordAsteroidSync } from './AsteroidSyncLog';
import type { AsteroidCorrection } from './AsteroidSyncLog';

let clientWorld: RapierWorld | null = null;
let clientWorldReady = false;
let localEntityId: number | null = null;

// Saved asteroid positions for snapshot-frame save/restore.
// On snapshot frames, the forced prediction tick advances ALL bodies including asteroids.
// This causes asteroids to be 1 tick ahead of the server (client: 0+1+3=4 ticks vs server: 3 ticks
// between syncs), creating a constant position offset that manifests as 20Hz jitter.
// saveAsteroidSyncState() captures positions BEFORE the forced tick, and
// restoreAsteroidSyncState() undoes the advancement AFTER the tick loop.
let savedAsteroidSyncSnapshot: Array<{ handle: number; x: number; y: number; vx: number; vy: number }> | null = null;

// Latest server-authoritative positions for remote ships.
// Used to reset remote ship bodies when a new snapshot arrives (syncRemoteShips).
const remoteShipStates: Map<number, { x: number; y: number; vx: number; vy: number; rotation: number }> = new Map();

// Reconstructed input state for remote ships.
// Battery tracks across ticks (affects afterburner availability).
// Input bitfield is reconstructed from snapshot flags (thrusting/afterburner/braking).
// Rotation direction is unknown — held at 0.
const remoteShipInputs: Map<number, { battery: number; inputBitfield: number }> = new Map();

// Server-authoritative moon angle for client extrapolation.
// Instead of computing moonAngle = tick * dt * speed (which diverges due to
// floating-point accumulation vs multiplication), the client receives the
// server's accumulated moonAngle in each snapshot and extrapolates forward.
let serverMoonAngle = 0;
let serverMoonTick = 0;


/**
 * Initialize client physics with world data from the server's WorldSetup message.
 */
export async function initClientPhysics(setup: WorldSetup, entityId: number): Promise<void> {
  await initRapier();

  if (clientWorld) {
    clientWorld.destroy();
  }

  clientWorld = new RapierWorld();
  localEntityId = entityId;

  // Add boundary walls
  clientWorld.addBoundaryWalls();

  // Add planet (static)
  clientWorld.addCelestialBody(
    { x: setup.planet.x, y: setup.planet.y, radius: setup.planet.radius, mass: setup.planet.mass },
    false,
  );

  // Add moon (kinematic) at initial orbit position (angle=0)
  clientWorld.addCelestialBody(
    {
      x: PLANET_X + setup.moon.orbitRadius,
      y: PLANET_Y,
      radius: setup.moon.radius,
      mass: setup.moon.mass,
    },
    true,
  );

  // Add asteroids (with server-provided velocities)
  for (const a of setup.asteroids) {
    clientWorld.addAsteroid(a.x, a.y, a.radius, a.vx, a.vy, a.radius * ASTEROID_MASS_PER_RADIUS);
  }

  clientWorldReady = true;
}

/**
 * Ensure the local ship exists in the client physics world.
 * Called before the first prediction tick.
 */
export function ensureLocalShip(entityId: number, x: number, y: number, rotation: number): void {
  if (!clientWorld || !clientWorldReady) return;
  if (clientWorld.hasShip(entityId)) return;
  // Ship-asteroid collision enabled on client. 20Hz asteroid sync keeps positions
  // accurate enough for reliable collision prediction (same pattern as remote ships).
  clientWorld.addShip(entityId, x, y, rotation);
}

/**
 * Sync remote ship physics bodies from the latest snapshot data.
 * For each remote player: creates a Rapier body if missing, then resets to server position.
 * Removes bodies for players no longer in the map (disconnected).
 */
export function syncRemoteShips(remotePlayers: ReadonlyMap<number, RemotePlayer>): void {
  if (!clientWorld || !clientWorldReady) return;

  // Add/update remote ship bodies
  for (const [entityId, remote] of remotePlayers) {
    // Skip the local player
    if (entityId === localEntityId) continue;

    const s = remote.state;

    // Create body if it doesn't exist yet
    if (!clientWorld.hasShip(entityId)) {
      clientWorld.addShip(entityId, s.x, s.y, s.rotation);
    }

    // Store latest server state for resetRemoteShips()
    remoteShipStates.set(entityId, {
      x: s.x,
      y: s.y,
      vx: s.vx,
      vy: s.vy,
      rotation: s.rotation,
    });

    // Reconstruct input bitfield from snapshot flags.
    let inputBitfield = 0;
    if (s.thrusting) inputBitfield |= INPUT_THRUST;
    if (s.afterburner) inputBitfield |= INPUT_AFTERBURNER;
    if (s.braking) inputBitfield |= INPUT_BRAKE;
    if (s.strafingLeft) inputBitfield |= INPUT_STRAFE_LEFT;
    if (s.strafingRight) inputBitfield |= INPUT_STRAFE_RIGHT;
    if (s.firing) inputBitfield |= INPUT_FIRE;

    remoteShipInputs.set(entityId, {
      battery: s.battery,
      inputBitfield,
    });

    // Reset body to server position immediately (authoritative correction point)
    clientWorld.resetShipState(entityId, s.x, s.y, s.vx, s.vy, s.rotation);
  }

  // Remove bodies for players who left
  const activeRemoteIds = new Set<number>();
  for (const [entityId] of remotePlayers) {
    if (entityId !== localEntityId) activeRemoteIds.add(entityId);
  }
  for (const entityId of remoteShipStates.keys()) {
    if (!activeRemoteIds.has(entityId)) {
      clientWorld.removeShip(entityId);
      remoteShipStates.delete(entityId);
      remoteShipInputs.delete(entityId);
    }
  }
}

/**
 * Remove a single remote ship from the physics world.
 * Called when MSG_PLAYER_LEAVE is received.
 */
export function removeRemoteShip(entityId: number): void {
  if (clientWorld) {
    clientWorld.removeShip(entityId);
  }
  remoteShipStates.delete(entityId);
  remoteShipInputs.delete(entityId);
}

/**
 * Reset all remote ship bodies to their latest server-authoritative positions.
 * Called once before reconciliation replay loop (remote ships then advance
 * naturally with inputs + gravity during replay).
 */
function resetRemoteShips(): void {
  if (!clientWorld) return;
  for (const [entityId, state] of remoteShipStates) {
    clientWorld.resetShipState(entityId, state.x, state.y, state.vx, state.vy, state.rotation);
  }
}

/**
 * Apply reconstructed inputs to all remote ships.
 * Mirrors the server's per-ship input resolution in GameLoop.ts:
 *   resolveInput → applyShipInput → (gravity → step handled by caller).
 * Battery is tracked across ticks so afterburner drain/regen matches the server.
 */
function applyRemoteShipInputs(): void {
  if (!clientWorld) return;
  for (const [entityId, inputState] of remoteShipInputs) {
    const shipBody = clientWorld.extractShipState(entityId);
    if (!shipBody) continue;

    // Pass current rotation as targetAngle — remote ship's target is unknown,
    // so rotation stays unchanged (rotationDelta ≈ 0). Acceptable at 20Hz sync.
    const resolved = resolveInput(inputState.inputBitfield, shipBody.rotation, inputState.battery, shipBody.rotation);
    applyShipInput(clientWorld, entityId, resolved.force, resolved.rotationDelta, shipBody.rotation);
    inputState.battery = resolved.newBattery;
  }
}

/**
 * Set the server-authoritative moon angle from a snapshot.
 * Called by NetworkManager when a snapshot arrives.
 */
export function setServerMoonAngle(angle: number, tick: number): void {
  serverMoonAngle = angle;
  serverMoonTick = tick;
}

/**
 * Get the extrapolated moon angle for the given client tick.
 * Used by Game.tsx to update the visual moon angle at 60Hz tick rate
 * instead of 20Hz snapshot rate, eliminating visible stepping.
 */
export function getExtrapolatedMoonAngle(clientTick: number): number {
  return serverMoonAngle + (clientTick - serverMoonTick) * TICK_DELTA * MOON_ORBIT_SPEED;
}

/**
 * Update the client moon position for the given tick.
 * Extrapolates forward from the last server-authoritative moonAngle
 * rather than computing from scratch (tick * dt * speed), which eliminates
 * floating-point divergence between server accumulation and client multiplication.
 */
function updateClientMoon(clientTick: number): void {
  if (!clientWorld) return;
  const moonAngle = serverMoonAngle + (clientTick - serverMoonTick) * TICK_DELTA * MOON_ORBIT_SPEED;
  const moonX = PLANET_X + Math.cos(moonAngle) * MOON_ORBIT_RADIUS;
  const moonY = PLANET_Y + Math.sin(moonAngle) * MOON_ORBIT_RADIUS;
  clientWorld.updateMoonOrbit(moonX, moonY);
}

/**
 * Run one prediction tick with current input.
 * Returns the predicted local ship state.
 */
export function predictTick(
  currentShip: ShipState,
  inputBitfield: number,
  clientTick: number,
  targetAngle: number,
): ShipState {
  if (!clientWorld || !clientWorldReady || localEntityId === null) {
    return currentShip;
  }

  // Reset ship to current predicted position
  clientWorld.resetShipState(
    localEntityId,
    currentShip.x,
    currentShip.y,
    currentShip.vx,
    currentShip.vy,
    currentShip.rotation,
  );

  // Remote ships are NOT reset here — they advance naturally between snapshots
  // (matching server behavior where all ships simulate every tick).
  // They are only reset when a new snapshot arrives in syncRemoteShips().

  // Update moon orbit
  updateClientMoon(clientTick);

  // Apply remote ship inputs (mirrors server: resolve input → apply forces per ship)
  applyRemoteShipInputs();

  // Resolve local input (mouse-aim: rotation chases targetAngle)
  const resolved = resolveInput(inputBitfield, currentShip.rotation, currentShip.battery, targetAngle);

  // Apply local input forces
  applyShipInput(clientWorld, localEntityId, resolved.force, resolved.rotationDelta, currentShip.rotation);

  // Apply gravity
  clientWorld.applyGravity();

  // Step physics
  clientWorld.step();

  // Extract result
  const ship: MutableShipState = { ...currentShip };
  return extractShipAfterStep(
    clientWorld,
    ship,
    resolved.newBattery,
    resolved.isAfterburning,
    resolved.isThrusting,
    resolved.isBraking,
    resolved.isStrafingLeft,
    resolved.isStrafingRight,
    resolved.isFiring,
  );
}

/**
 * Diagnostic context passed from Game.tsx to reconcile() for logging.
 * Contains data only available to the caller (clientTick, localShip before, timing).
 */
export interface ReconcileDiagnostics {
  clientTickBefore: number;
  clientShipBefore: ShipState;
  lastAckedInput: number;
  reconcileStartMs: number;
}

/**
 * Perform server reconciliation:
 * 1. Start from server's authoritative state
 * 2. Re-apply all unacked inputs
 * 3. Return the predicted state
 */
export function reconcile(
  serverState: ShipState,
  pendingInputs: ReadonlyArray<InputFrame>,
  serverTick: number,
  diagnostics?: ReconcileDiagnostics,
): ShipState {
  if (!clientWorld || !clientWorldReady || localEntityId === null) {
    return serverState;
  }

  // Reset to server authoritative state
  clientWorld.resetShipState(
    localEntityId,
    serverState.x,
    serverState.y,
    serverState.vx,
    serverState.vy,
    serverState.rotation,
  );

  // Snapshot asteroid positions before replay. Asteroids collide with ships during
  // replay, so their positions matter for reconciliation accuracy. Restoring after
  // replay prevents extra physics steps from drifting asteroid positions for
  // subsequent prediction ticks. 20Hz sync keeps them accurate.
  const asteroidSnapshot = clientWorld.snapshotAsteroidStates();

  // Reset remote ships ONCE before replay loop. During replay, they advance
  // naturally with inputs + gravity (just like on the server). Previously they
  // were restored to snapshot positions after reconciliation, but now they end
  // at their predicted position which IS the correct state for subsequent ticks.
  resetRemoteShips();

  // Note: remoteShipInputs.battery is already at the snapshot value because
  // syncRemoteShips() runs before reconcile() (Game.tsx ordering fix).
  // applyRemoteShipInputs() will drain/regen battery during replay, leaving
  // it at the correct post-replay value for subsequent prediction ticks.

  let ship: MutableShipState = { ...serverState };
  // Server snapshot at tick N = state AFTER processing tick N.
  // First pending input will be processed at tick N+1 with moon at angle_N+1.
  let tick = serverTick + 1;

  // Reset moon to exact server position before replay. setNextKinematicTranslation()
  // computes velocity = (nextPos - currentPos) / dt, so the moon needs to be at the
  // correct serverTick position before the first updateClientMoon(serverTick+1) call.
  const serverMoonX = PLANET_X + Math.cos(serverMoonAngle) * MOON_ORBIT_RADIUS;
  const serverMoonY = PLANET_Y + Math.sin(serverMoonAngle) * MOON_ORBIT_RADIUS;
  clientWorld.resetMoonPosition(serverMoonX, serverMoonY);

  // Ship-asteroid collision stays ENABLED during reconciliation replay. With 20Hz
  // asteroid sync (same rate as snapshots), asteroid positions at replay start closely
  // match the server's. Both replay and prediction compute consistent bounce results,
  // eliminating the rubber-banding caused by collision-on (predict) vs collision-off (replay).
  for (const input of pendingInputs) {
    // Update moon for this tick
    updateClientMoon(tick);

    // Apply remote ship inputs (mirrors server: all ships get input each tick)
    applyRemoteShipInputs();

    // Resolve local input (mouse-aim: rotation chases targetAngle from stored InputFrame)
    const resolved = resolveInput(input.bitfield, ship.rotation, ship.battery, input.targetAngle);

    // Apply local input forces
    applyShipInput(clientWorld, localEntityId, resolved.force, resolved.rotationDelta, ship.rotation);

    // Apply gravity + step
    clientWorld.applyGravity();
    clientWorld.step();

    // Extract result
    ship = extractShipAfterStep(
      clientWorld,
      ship,
      resolved.newBattery,
      resolved.isAfterburning,
      resolved.isThrusting,
      resolved.isBraking,
      resolved.isStrafingLeft,
      resolved.isStrafingRight,
      resolved.isFiring,
    );

    tick++;
  }

  // Restore asteroids to pre-replay positions. Replay advances asteroid positions
  // via gravity and ship collisions beyond where they should be for subsequent
  // prediction ticks. Restoring ensures prediction ticks start from synced positions.
  // 20Hz asteroid sync (syncAsteroidPositions) keeps them accurate.
  clientWorld.restoreAsteroidStates(asteroidSnapshot);

  // Fix post-reconciliation ship-asteroid overlap. After replaying pending inputs,
  // the ship may overlap an asteroid due to asteroid restore shifting relative
  // ship-asteroid positions. Push ship to the asteroid surface (position-only correction).
  if (clientWorld.depenetrateShipFromAsteroids(localEntityId)) {
    const corrected = clientWorld.extractShipState(localEntityId);
    if (corrected) {
      ship.x = corrected.x;
      ship.y = corrected.y;
      ship.vx = corrected.vx;
      ship.vy = corrected.vy;
    }
  }

  // Fix post-reconciliation ship-celestial overlap (moon stuck safety net).
  // After replay, the ship may be inside the moon due to gravity/collision mismatch.
  // Push ship to the celestial surface (position-only correction).
  if (clientWorld.depenetrateShipFromCelestials(localEntityId)) {
    const corrected = clientWorld.extractShipState(localEntityId);
    if (corrected) {
      ship.x = corrected.x;
      ship.y = corrected.y;
      ship.vx = corrected.vx;
      ship.vy = corrected.vy;
    }
  }

  // Remote ships are NOT restored — they end at their predicted position after
  // replay (with inputs + gravity), which is the correct state for subsequent
  // prediction ticks. This matches the server where all ships advance continuously.

  // Record reconciliation event for diagnostics
  if (diagnostics) {
    const clientTickAfter = serverTick + pendingInputs.length;
    const dxDesync = diagnostics.clientShipBefore.x - serverState.x;
    const dyDesync = diagnostics.clientShipBefore.y - serverState.y;
    const desyncDistance = Math.sqrt(dxDesync * dxDesync + dyDesync * dyDesync);
    const dxReconciled = ship.x - serverState.x;
    const dyReconciled = ship.y - serverState.y;
    const reconciledDistance = Math.sqrt(dxReconciled * dxReconciled + dyReconciled * dyReconciled);
    const extrapolatedMoonAngle = serverMoonAngle + (diagnostics.clientTickBefore - serverMoonTick) * TICK_DELTA * MOON_ORBIT_SPEED;

    recordReconciliation({
      timestamp: performance.now(),
      serverTick,
      clientTickBefore: diagnostics.clientTickBefore,
      clientTickAfter,
      pendingInputCount: pendingInputs.length,
      serverShipX: serverState.x,
      serverShipY: serverState.y,
      serverShipVx: serverState.vx,
      serverShipVy: serverState.vy,
      serverShipRotation: serverState.rotation,
      serverShipBattery: serverState.battery,
      clientShipBeforeX: diagnostics.clientShipBefore.x,
      clientShipBeforeY: diagnostics.clientShipBefore.y,
      clientShipBeforeVx: diagnostics.clientShipBefore.vx,
      clientShipBeforeVy: diagnostics.clientShipBefore.vy,
      clientShipBeforeRotation: diagnostics.clientShipBefore.rotation,
      clientShipBeforeBattery: diagnostics.clientShipBefore.battery,
      reconciledShipX: ship.x,
      reconciledShipY: ship.y,
      reconciledShipVx: ship.vx,
      reconciledShipVy: ship.vy,
      reconciledShipRotation: ship.rotation,
      reconciledShipBattery: ship.battery,
      desyncDistance,
      reconciledDistance,
      reconcileMs: performance.now() - diagnostics.reconcileStartMs,
      serverMoonAngle,
      extrapolatedMoonAngle,
      lastAckedInput: diagnostics.lastAckedInput,
      oldestPendingSeq: pendingInputs.length > 0 ? pendingInputs[0].sequence : 0,
      newestPendingSeq: pendingInputs.length > 0 ? pendingInputs[pendingInputs.length - 1].sequence : 0,
    });
  }

  return ship;
}

/**
 * Extract predicted positions for all remote ships from the Rapier physics world.
 * These positions reflect the simulation with inputs + gravity (matching server).
 * Called after the tick loop to pipe positions to the rendering layer.
 */
export function getRemoteShipPredictedStates(): Map<number, { x: number; y: number; vx: number; vy: number; rotation: number }> {
  const result = new Map<number, { x: number; y: number; vx: number; vy: number; rotation: number }>();
  if (!clientWorld || !clientWorldReady) return result;
  for (const entityId of remoteShipStates.keys()) {
    const state = clientWorld.extractShipState(entityId);
    if (state) {
      result.set(entityId, { x: state.x, y: state.y, vx: state.vx, vy: state.vy, rotation: state.rotation });
    }
  }
  return result;
}

/**
 * Extract current asteroid positions from the client physics world.
 * Called each tick to keep visual in sync with physics.
 */
export function getAsteroidPositions(): Array<{ x: number; y: number; radius: number; vx: number; vy: number }> {
  if (!clientWorld || !clientWorldReady) return [];
  const states = clientWorld.extractAsteroidStates();
  return states.map((s) => ({ x: s.x, y: s.y, radius: s.radius, vx: s.vx, vy: s.vy }));
}

/**
 * Save asteroid positions BEFORE the forced prediction tick on snapshot frames.
 * On snapshot frames, the game loop sets accumulator = TICK_DELTA_MS, guaranteeing
 * exactly 1 prediction tick. This tick advances ALL bodies including asteroids,
 * putting them 1 tick ahead of the server. Saving before and restoring after
 * eliminates the 1-tick offset that causes 20Hz jitter on moving asteroids.
 *
 * The prediction tick itself still uses correct asteroid positions for ship-asteroid
 * collision — only after the tick are positions restored for rendering.
 */
export function saveAsteroidSyncState(): void {
  if (!clientWorld || !clientWorldReady) return;
  savedAsteroidSyncSnapshot = clientWorld.snapshotAsteroidStates();
}

/**
 * Restore asteroid positions AFTER the prediction tick loop on snapshot frames.
 * Undoes the 1-tick advancement caused by the forced prediction tick.
 * Ship collision response from the tick is already applied to the ship's velocity
 * and is preserved — only asteroid positions are rolled back.
 */
export function restoreAsteroidSyncState(): void {
  if (!clientWorld || !clientWorldReady || !savedAsteroidSyncSnapshot) return;
  clientWorld.restoreAsteroidStates(savedAsteroidSyncSnapshot);
  savedAsteroidSyncSnapshot = null;
}

/**
 * Sync asteroid positions/velocities from server — always snap to exact server state.
 * At 20Hz sync (same as snapshots), position divergence is bounded to ~50ms of
 * N-body drift, small enough that snapping is imperceptible and gives maximum
 * accuracy for client-side collision prediction.
 * Called when an asteroid sync message arrives from the server (20Hz).
 */
export function syncAsteroidPositions(
  serverAsteroids: ReadonlyArray<{ x: number; y: number; vx: number; vy: number }>,
  serverTick?: number,
): void {
  if (!clientWorld || !clientWorldReady) return;
  const snapshot = clientWorld.snapshotAsteroidStates();
  const corrections: Array<{ handle: number; x: number; y: number; vx: number; vy: number }> = [];

  // Collect per-asteroid diagnostics for the sync log
  const logCorrections: AsteroidCorrection[] = [];
  let maxPositionDelta = 0;

  for (let i = 0; i < snapshot.length && i < serverAsteroids.length; i++) {
    const dx = serverAsteroids[i].x - snapshot[i].x;
    const dy = serverAsteroids[i].y - snapshot[i].y;
    const positionDelta = Math.sqrt(dx * dx + dy * dy);

    const dvx = serverAsteroids[i].vx - snapshot[i].vx;
    const dvy = serverAsteroids[i].vy - snapshot[i].vy;
    const velocityDiff = Math.sqrt(dvx * dvx + dvy * dvy);

    if (positionDelta > maxPositionDelta) maxPositionDelta = positionDelta;

    // Snap position and velocity exactly to server values
    corrections.push({
      handle: snapshot[i].handle,
      x: serverAsteroids[i].x,
      y: serverAsteroids[i].y,
      vx: serverAsteroids[i].vx,
      vy: serverAsteroids[i].vy,
    });

    logCorrections.push({
      index: i,
      clientX: snapshot[i].x,
      clientY: snapshot[i].y,
      serverX: serverAsteroids[i].x,
      serverY: serverAsteroids[i].y,
      clientVx: snapshot[i].vx,
      clientVy: snapshot[i].vy,
      serverVx: serverAsteroids[i].vx,
      serverVy: serverAsteroids[i].vy,
      positionDelta,
      velocityDelta: velocityDiff,
      wasSnap: true,
    });
  }

  clientWorld.restoreAsteroidStates(corrections);

  // Record asteroid sync event for diagnostics
  recordAsteroidSync({
    timestamp: performance.now(),
    serverTick: serverTick ?? 0,
    asteroidCount: Math.min(snapshot.length, serverAsteroids.length),
    corrections: logCorrections,
    maxPositionDelta,
    snapCount: Math.min(snapshot.length, serverAsteroids.length),
    lerpCount: 0,
  });
}

/**
 * Export full debug state of the client physics world.
 * Returns null if the world is not initialized.
 */
export function debugExportClientState(): DebugPhysicsState | null {
  if (!clientWorld || !clientWorldReady) return null;
  return clientWorld.debugExport();
}

/**
 * Export prediction module internal state for debug diagnostics.
 * These values are critical for diagnosing moon/tick desync issues.
 */
export function debugExportPredictionState(): {
  serverMoonAngle: number;
  serverMoonTick: number;
  clientWorldReady: boolean;
  localEntityId: number | null;
} {
  return {
    serverMoonAngle,
    serverMoonTick,
    clientWorldReady,
    localEntityId,
  };
}

/**
 * Destroy client physics world and free resources.
 */
export function destroyClientPhysics(): void {
  if (clientWorld) {
    clientWorld.destroy();
    clientWorld = null;
  }
  clientWorldReady = false;
  localEntityId = null;
  serverMoonAngle = 0;
  serverMoonTick = 0;
  remoteShipStates.clear();
  remoteShipInputs.clear();
}
